import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { loadDataCredentials, loadVendors } from 'js/actions';
import { CONNECTION_CATEGORY_FILE } from 'js/constants/connectionCategories';
import { selectCurrentOrgSafe } from 'new/hooks/adapters/useCurrentOrg';
import usePaging from 'new/state/paging/repository/usePaging';

export interface StudioFileConnectors {
  orgId: any;
  /** File-only source connector keys. */
  fileSources: string[];
  /** File-only sink connector keys. */
  fileSinks: string[];
}

/**
 * Loads vendors + credentials (so the real SelectConnection/EnterCredential
 * render) and derives the file-only connector lists + org id. Shared by the
 * Studio create wizard ({@link StudioDraftProvider}) and the edit-from-canvas
 * modals so both surface the identical connector set.
 */
export function useStudioFileConnectors(): StudioFileConnectors {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(loadVendors() as any);
    dispatch(loadDataCredentials() as any);
  }, [dispatch]);

  const org = useSelector(selectCurrentOrgSafe);
  const orgId = org?.id;

  const { paging, getConnectionCategoryByConnectorType } = usePaging();
  const sourcesSupported = paging?.sourcesSupported;
  const sinksSupported = paging?.sinksSupported;

  const fileSources = useMemo(
    () =>
      (sourcesSupported ?? []).filter(
        (t: string) => getConnectionCategoryByConnectorType(t) === CONNECTION_CATEGORY_FILE,
      ),
    [sourcesSupported, getConnectionCategoryByConnectorType],
  );
  const fileSinks = useMemo(
    () =>
      (sinksSupported ?? []).filter(
        (t: string) => getConnectionCategoryByConnectorType(t) === CONNECTION_CATEGORY_FILE,
      ),
    [sinksSupported, getConnectionCategoryByConnectorType],
  );

  return { orgId, fileSources, fileSinks };
}

export default useStudioFileConnectors;
